import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CustomError, MandateRequest, MandateType } from 'types';
import { saveTemporaryCase } from 'services/mandate/saveTemporaryCase';
import { closeModal, openModal } from './modalReducer';
import { Case } from 'pco-product-service-internal-consumer';

export const initialMandateState: MandateType = {
  isLoading: false,
  mandateRequest: {
    isRequiredNewBr: false,
  },
};

export const saveTemporaryCaseThunk = createAsyncThunk(
  'MANDATE/saveTemporaryCase',
  async (request: MandateRequest, thunkAPI) => {
    try {
      return await saveTemporaryCase(request as unknown as Case);
    } catch (error: unknown) {
      thunkAPI.dispatch(
        openModal({
          modalType: 'JBErrorModalWrapper',
          modalProps: {
            httpError: error,
            okHandler: () => {
              thunkAPI.dispatch(closeModal());
            },
            technicalInfo: {
              title: 'Error when saving temporary case',
              userErrorMessage: `An error occurred when saving temporary case. Please try again later.`,
            },
          },
        }),
      );
    }
  },
);

const mandateSlice = createSlice({
  name: 'MANDATE',
  initialState: initialMandateState,
  reducers: {
    setIsRequiredNewBr: (state, action: PayloadAction<boolean | undefined>) => {
      return {
        ...state,
        mandateRequest: {
          ...state.mandateRequest,
          isRequiredNewBr: action.payload,
        },
      };
    },
    setMandatePartnerRequestId: (state, action: PayloadAction<string | undefined>) => {
      return {
        ...state,
        mandateRequest: {
          ...state.mandateRequest,
          partnerNumberId: action.payload,
        },
      };
    },
    setPlaceHolderName: (state, action: PayloadAction<string | undefined>) => {
      return {
        ...state,
        mandateRequest: {
          ...state.mandateRequest,
          placeHolderName: action.payload,
        },
      };
    },
    setCaseId: (state, action: PayloadAction<number | undefined>) => {
      return {
        ...state,
        mandateRequest: { ...state.mandateRequest, caseId: action.payload },
      };
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      return { ...state, isLoading: action.payload };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(saveTemporaryCaseThunk.pending, (state) => {
      return { ...state, isLoading: true };
    });
    builder.addCase(saveTemporaryCaseThunk.fulfilled, (state, action) => {
      return {
        ...state,
        isLoading: false,
        mandateRequest: {
          ...state.mandateRequest,
          caseId: action.payload?.caseId,
        },
      };
    });
    builder.addCase(saveTemporaryCaseThunk.rejected, (state, action) => {
      return {
        ...state,
        isLoading: false,
        error: action.payload as CustomError,
      };
    });
  },
});

export const { setIsRequiredNewBr, setMandatePartnerRequestId, setPlaceHolderName, setCaseId, setIsLoading } =
  mandateSlice.actions;
export default mandateSlice.reducer;
